<template>
    <div>
        <!-- Section header -->
        <div class="dashboard-section-header mb-3 mt-5">
            <div class="d-flex align-items-center gap-2">
                <img :src="iconSpecialOk" alt="" class="dashboard-section-header__icon">
                <span class="dashboard-section-header__title">{{ $t('cert.myCertificates') }}</span>
                <span class="count-badge ms-2">{{ totalCount }}</span>
            </div>
            <router-link :to="{ name: 'CertList' }" class="dashboard-show-all">
                {{ $t('cert.viewAll') }}
                <i class="bi bi-chevron-right dashboard-show-all__chevron"></i>
            </router-link>
        </div>

        <!-- Loading -->
        <Skeletor v-if="loading" />

        <!-- Empty state -->
        <div v-if="!loading && !certsList.length" class="text-muted" style="font-size: 14px;">
            {{ $t('general.noRecord') }}
        </div>

        <!-- List -->
        <div v-if="!loading && certsList.length" class="cert-list mb-5">
            <div
                class="cert-list__row"
                v-for="cert in certsList.slice(0, 5)"
                :key="cert.uuid"
            >
                <!-- Icon -->
                <div class="cert-list__icon-wrap">
                    <i :class="['bi', certFileIcon(cert).cls, 'cert-list__file-icon']" :style="{ color: certFileIcon(cert).color }"></i>
                </div>

                <!-- Name -->
                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('cert.certName') }}</span>
                    <span class="cert-list__value">{{ cert.name }}</span>
                </div>

                <!-- Created -->
                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('cert.created') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(cert.created_at) }}, {{ formatTime(cert.created_at) }}</span>
                </div>

                <!-- Badge -->
                <div v-if=false class="cert-list__badge-col">
                    <div class="shared-cert-badge shared-cert-badge--ok">
                        <i class="bi bi-check-lg shared-cert-badge__icon"></i>
                        <span>No changes</span>
                    </div>
                </div>

                <!-- Action -->
                <div class="cert-list__action">
                    <span
                        v-if="['link', 'logged'].includes(cert.file?.sharing_settings?.mode)"
                        class="cert-list__sharing-icon"
                        :style="{ maskImage: `url(${iconSharingChain})`, webkitMaskImage: `url(${iconSharingChain})` }"
                    ></span>
                    <router-link
                        class="btn cert-list__view-btn"
                        :to="{ name: 'CertDetail', params: { uuid: cert.uuid } }"
                    >
                        {{ $t('cert.viewCertificate') }}
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import http from '@/http';
import iconSpecialOk from '/public/img/new-design/icons/special-ok-icon.svg';
import iconSharingChain from '/public/img/new-design/icons/sharing.svg';

export default {
    mounted() {
        this.getCertsList();
        let self = this;
        this.emitter.on('certAdded', (newCert) => {
            self.certsList.unshift(newCert);
        });
    },

    data() {
        return {
            certsList: [],
            totalCount: 0,
            loading: true,
            iconSpecialOk,
            iconSharingChain,
        };
    },

    methods: {
        certFileIcon(cert) {
            const mime = cert.file?.mime_type ?? '';
            const ext = (cert.file?.extension || (cert.file?.filename ?? '').split('.').pop() || '').toLowerCase();
            if (mime === 'application/pdf' || ext === 'pdf')
                return { cls: 'bi-file-earmark-pdf', color: '#e74c3c' };
            if (mime.startsWith('image/') || ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','svg'].includes(ext))
                return { cls: 'bi-file-earmark-image', color: '#3498db' };
            if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc','docx','odt'].includes(ext))
                return { cls: 'bi-file-earmark-word', color: '#2b579a' };
            if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || ['xls','xlsx','ods','csv'].includes(ext))
                return { cls: 'bi-file-earmark-excel', color: '#217346' };
            if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint' || ['ppt','pptx','odp'].includes(ext))
                return { cls: 'bi-file-earmark-ppt', color: '#d24726' };
            if (mime.startsWith('audio/') || ['mp3','wav','ogg','flac','aac','m4a'].includes(ext))
                return { cls: 'bi-file-earmark-music', color: '#1abc9c' };
            if (mime.startsWith('video/') || ['mp4','avi','mov','mkv','webm','m4v'].includes(ext))
                return { cls: 'bi-file-earmark-play', color: '#e67e22' };
            if (['zip','rar','7z','tar','gz','bz2'].includes(ext) || mime.includes('zip'))
                return { cls: 'bi-file-earmark-zip', color: '#8e44ad' };
            if (mime.startsWith('text/') || ['txt','md','rtf'].includes(ext))
                return { cls: 'bi-file-earmark-text', color: '#7f8c8d' };
            if (['php','js','ts','py','html','css','json','xml','sql'].includes(ext))
                return { cls: 'bi-file-earmark-code', color: '#27ae60' };
            return { cls: 'bi-file-earmark', color: '#6B7280' };
        },

        getCertsList() {
            let self = this;
            http.get('/certs/list').then((response) => {
                self.certsList = response.data.userCerts;
                self.totalCount = response.data.meta?.totalRecordsCount ?? response.data.userCerts.length;
                self.loading = false;
            });
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>